import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import productApi from '@/lib/productApi';
import { inlineError, type InlineError } from '@/lib/errorReport';
import type { ProductListItem } from '@/types/product';
import {
  isAddable,
  pickedProducts,
  toggleSelection,
  withSelection,
  type PickerEntry,
} from './productPickerSelection';

export const ALL_PERIODS = '__all__';

export function useProductPicker(open: boolean) {
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState(ALL_PERIODS);
  const [entries, setEntries] = useState<Record<string, PickerEntry>>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogRevision, setCatalogRevision] = useState(0);
  const [error, setError] = useState<InlineError | null>(null);
  const entriesRef = useRef<Record<string, PickerEntry>>({});
  const requestIds = useRef<Record<string, number>>({});
  const generation = useRef(0);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    []
  );

  const writeEntries = useCallback((next: Record<string, PickerEntry>) => {
    entriesRef.current = next;
    setEntries(next);
  }, []);

  const patchEntry = useCallback(
    (id: string, patch: Partial<PickerEntry>) => {
      const current = entriesRef.current[id];
      if (!current) return;
      writeEntries({ ...entriesRef.current, [id]: { ...current, ...patch } });
    },
    [writeEntries]
  );

  const dropEntry = useCallback(
    (id: string) => {
      const rest = { ...entriesRef.current };
      delete rest[id];
      writeEntries(rest);
      setSelectedIds((prev) => prev.filter((entryId) => entryId !== id));
    },
    [writeEntries]
  );

  /** Haalt varianten en prijs op; doet niets als het product al geladen is. */
  const loadEntry = useCallback(
    async (product: ProductListItem) => {
      if (entriesRef.current[product.id]) return;

      const session = generation.current;
      const request = (requestIds.current[product.id] ?? 0) + 1;
      requestIds.current[product.id] = request;
      writeEntries({
        ...entriesRef.current,
        [product.id]: {
          product,
          snapshot: null,
          variants: [],
          variantId: '',
          quantity: '1',
          discount: '0',
          customPrice: '',
          loading: true,
        },
      });

      try {
        const detail = await productApi.get(product.id);
        if (generation.current !== session || requestIds.current[product.id] !== request) return;

        const variants = (detail.currentVersion?.payload.variants ?? []).map((variant) => ({
          id: variant.id,
          name: variant.name,
        }));
        const firstVariantId = variants[0]?.id ?? '';
        patchEntry(product.id, { variants, variantId: firstVariantId });

        const snapshot = await productApi.snapshot(product.id, firstVariantId || undefined);
        if (generation.current !== session || requestIds.current[product.id] !== request) return;
        patchEntry(product.id, { snapshot, loading: false });
      } catch (caught) {
        if (generation.current !== session || requestIds.current[product.id] !== request) return;
        dropEntry(product.id);
        setError(inlineError('De productdetails konden niet worden geladen. Probeer het opnieuw.', caught));
      }
    },
    [dropEntry, patchEntry, writeEntries]
  );

  const changeVariant = useCallback(
    async (productId: string, nextVariantId: string) => {
      const entry = entriesRef.current[productId];
      if (!entry || entry.loading || nextVariantId === entry.variantId) return;

      const session = generation.current;
      const request = (requestIds.current[productId] ?? 0) + 1;
      requestIds.current[productId] = request;
      patchEntry(productId, { variantId: nextVariantId, loading: true });
      setError(null);

      try {
        const snapshot = await productApi.snapshot(productId, nextVariantId);
        if (generation.current !== session || requestIds.current[productId] !== request) return;
        patchEntry(productId, { snapshot, loading: false });
      } catch (caught) {
        if (generation.current !== session || requestIds.current[productId] !== request) return;
        patchEntry(productId, { variantId: entry.variantId, loading: false });
        setError(inlineError('De gekozen variant kon niet worden geladen. Probeer het opnieuw.', caught));
      }
    },
    [patchEntry]
  );

  /** Toont het product rechts én vinkt het aan: één klik blijft één product. */
  const chooseProduct = useCallback(
    (product: ProductListItem) => {
      setError(null);
      setSelectedIds((prev) => withSelection(prev, product.id));
      void loadEntry(product);
    },
    [loadEntry]
  );

  const toggleProduct = useCallback(
    (product: ProductListItem) => {
      setError(null);
      setSelectedIds((prev) => toggleSelection(prev, product.id));
      void loadEntry(product);
    },
    [loadEntry]
  );

  useEffect(() => {
    if (!open) return;

    let active = true;
    setCatalogLoading(true);
    setError(null);
    productApi
      .list()
      .then((list) => {
        if (!active) return;
        const available = list.filter((product) => product.published && product.enabled && !product.archivedAt);
        setProducts(available);
      })
      .catch((caught) => {
        if (!active) return;
        setProducts([]);
        setError(inlineError('De productcatalogus kon niet worden geladen. Probeer het opnieuw.', caught));
      })
      .finally(() => {
        if (active) setCatalogLoading(false);
      });

    return () => {
      active = false;
    };
  }, [catalogRevision, loadEntry, open]);

  useEffect(() => {
    if (open) return;
    generation.current += 1;
    requestIds.current = {};
    setSearch('');
    setPeriod(ALL_PERIODS);
    writeEntries({});
    setSelectedIds([]);
    setError(null);
  }, [open, writeEntries]);

  const categories = useMemo(() => {
    const counts = new Map<string, number>();
    for (const product of products) {
      const name = product.category ?? 'Zonder categorie';
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    // Niet alfabetiseren: de producten komen in sheetvolgorde binnen, dus de
    // volgorde waarin een categorie voor het eerst opduikt ís de volgorde van
    // de secties in de Excel. Alleen de restgroep zakt naar onderen.
    return [...counts.entries()].sort(([a], [b]) => {
      if (a === 'Zonder categorie') return 1;
      if (b === 'Zonder categorie') return -1;
      return 0;
    });
  }, [products]);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('nl');
    return products.filter((product) => {
      const matchesPeriod = period === ALL_PERIODS || product.billingPeriod === period;
      const haystack = [
        product.name,
        product.description,
        product.category,
        ...product.labels,
        ...(product.variantNames ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLocaleLowerCase('nl');
      return matchesPeriod && (!query || haystack.includes(query));
    });
  }, [period, products, search]);

  const selectedEntries = selectedIds.map((id) => entries[id]).filter(Boolean);
  const canAdd =
    selectedIds.length > 0 &&
    selectedEntries.length === selectedIds.length &&
    selectedEntries.every(isAddable);
  return {
    retryCatalog: () => setCatalogRevision((value) => value + 1),
    products,
    search,
    setSearch,
    period,
    setPeriod,
    entries,
    selectedIds,
    selectedEntries,
    categories,
    filteredProducts,
    catalogLoading,
    error,
    setError,
    chooseProduct,
    toggleProduct,
    changeVariant,
    patchEntry,
    canAdd,
    picks: () => pickedProducts(selectedEntries.filter(isAddable)),
  };
}
